import { useState } from 'react'
import { AlertTriangle, FolderOpen, Search } from 'lucide-react'
import { ingestionService, type MatchPreview } from '@/services/ingestion'
import { formatUsdOrDash } from '@/utils/money'

/**
 * A record off the pipeline that carries no hold reason (stress test INT-10,
 * PKG-01). A reason is saved only since the release that began saving it, so a
 * record held earlier has none — and the pane drew nothing, which reads as "no
 * reason exists". This says the reason is unknown and never offers one.
 *
 * The check reads the pipeline through the same match preview Create Deal runs
 * before it creates anything (`GET /ingestion/{id}/match-preview`): read-only,
 * on today's rules. What it finds is not why the record was held then, and the
 * pane says so.
 */
export default function HoldReasonNotRecorded({
  ingestionId,
  detail,
  hasReingest,
  onOpenDeal,
}: {
  ingestionId: string
  /** The server's statement of the gap. */
  detail: string
  /** The record also carries a re-ingest match captured when it was held. */
  hasReingest?: boolean
  onOpenDeal?: (loanId: string) => void
}) {
  const [checking, setChecking] = useState(false)
  const [preview, setPreview] = useState<MatchPreview | null>(null)
  const [error, setError] = useState<string | null>(null)

  const check = async () => {
    setChecking(true)
    setError(null)
    try {
      setPreview(await ingestionService.getMatchPreview(ingestionId))
    } catch {
      setPreview(null)
      setError('Could not check the pipeline just now. Please retry.')
    } finally {
      setChecking(false)
    }
  }

  const matches = preview?.candidates ?? []

  return (
    <div
      data-testid="hold-reason-not-recorded"
      className="flex items-start gap-2 rounded border border-amber-400/25 bg-amber-400/[0.05] px-2.5 py-2"
    >
      <AlertTriangle size={12} className="mt-px flex-shrink-0 text-amber-300" />
      <div className="min-w-0">
        <div className="text-[11px] font-medium text-amber-200">
          Not a deal yet — hold reason not recorded
        </div>
        <div className="text-[10px] text-stone-400">{detail}</div>
        {hasReingest && (
          <div className="mt-0.5 text-[10px] text-stone-400">
            The re-ingest match below was captured when the record was held; it has not been re-checked.
          </div>
        )}
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={check}
            disabled={checking}
            className="btn-secondary flex items-center gap-1.5 px-3 py-1 text-[10px] disabled:opacity-50"
          >
            <Search size={10} />
            {checking ? 'Checking…' : 'Check for a matching deal now'}
          </button>
          <span className="text-[9px] text-stone-600">
            Reads the pipeline as it stands today. Nothing is created or changed.
          </span>
        </div>
        {error && (
          <p role="alert" className="mt-1 text-[10px] text-red-300">
            {error}
          </p>
        )}
        {preview && (
          <div data-testid="hold-recheck-result" className="mt-1.5 space-y-1">
            {matches.length === 0 ? (
              <p className="text-[10px] text-stone-300">No existing deal matches this record today.</p>
            ) : (
              <>
                <p className="text-[10px] text-stone-300">
                  {matches.length === 1 ? 'An existing deal matches' : `${matches.length} existing deals match`}{' '}
                  this record today. Create Deal shows {matches.length === 1 ? 'it' : 'them'} before anything is created.
                </p>
                {matches.map((c) => (
                  <div key={c.loanId} className="flex flex-wrap items-center gap-x-2 text-[10px] text-stone-300">
                    <span className="font-mono text-amber-300">{c.loanId}</span>
                    <span>{c.property || 'Unnamed deal'}</span>
                    <span className="text-stone-500">
                      {[c.location, formatUsdOrDash(c.loanAmount)].filter(Boolean).join(' · ')}
                    </span>
                    {onOpenDeal && (
                      <button
                        type="button"
                        onClick={() => onOpenDeal(c.loanId)}
                        aria-label={`Open deal ${c.loanId}`}
                        className="inline-flex items-center gap-1 text-amber-300 underline decoration-dotted hover:text-amber-200"
                      >
                        <FolderOpen size={10} />
                        Open deal
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}
            <p className="text-[9px] text-stone-500">
              Today's reading only — it does not recover why the record was held.
            </p>
          </div>
        )}
      </div>
    </div>
  )
}
